import React, { useEffect, useRef, useState } from "react";
import factoids from "../info/factoids";
import "../pages/solutions/Solutions.css"

const factoidColors = [
  "#8C277B",
  "#24BFBF",
  "#D94929",
  "#8FC54B",
];

function getFirstNumber(stat) {
  const match = stat.match(/\d+(\.\d+)?/);
  return match ? Number(match[0]) : null;
}

function CountUpStat({ stat }) {
  const ref = useRef(null);
  const [displayStat, setDisplayStat] = useState(stat);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const number = getFirstNumber(stat);

    if (number === null) {
      setDisplayStat(stat);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasAnimated) return;

        setHasAnimated(true);

        let startTime = null;
        const duration = 1200;

        const animate = (timestamp) => {
          if (!startTime) startTime = timestamp;

          const progress = Math.min((timestamp - startTime) / duration, 1);
          const current = number * progress;

          const rounded =
            number % 1 === 0 ? Math.round(current) : current.toFixed(1);

          const animatedStat = stat.replace(/\d+(\.\d+)?/, rounded);
          setDisplayStat(animatedStat);

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            setDisplayStat(stat);
          }
        };

        requestAnimationFrame(animate);
      },
      { threshold: 0.35 }
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, [stat, hasAnimated]);

  return (
    <div ref={ref} className="factoid-stat" data-stat={displayStat}>
      {displayStat}
    </div>
  );
}

export default function FactoidSection({
  solution,
  title,
  description,
  backgroundColor = "#f9f9f9",
}) {
  const pageFactoids = factoids.filter((item) => item.solution === solution);

  if (!pageFactoids.length) return null;

  return (
    <div style={{ backgroundColor }}>
      <div className="solutions-section-factoids liquid-factoids-section">
        {title && (
          <h2 className="solutions-section-factoids-header">
            {title}
          </h2>
        )}

        {description && (
          <p className="solution-section-1-description liquid-factoids-description">
            {description}
          </p>
        )}

        <div
          className={`liquid-factoid-grid ${
            pageFactoids.length === 3 ? "three-factoids" : ""
          }`}
        >
          {pageFactoids.map((item, index) => {
            const color = factoidColors[index % factoidColors.length];

            return (
              <div
                className="liquid-factoid-card-wrap"
                key={`${item.solution}-${item.stat}-${index}`}
                style={{ "--factoid-color": color }}
              >
                <CountUpStat stat={item.stat} />

                <div className="liquid-factoid-card">
                  <p className="liquid-factoid-text">{item.desc}</p>

                  {item.source && (
                    <p className="liquid-factoid-source">
                      Source:{" "}
                      {item.link ? (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item.source}
                        </a>
                      ) : (
                        item.source
                      )}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}